"use client";

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import getWorkshopConfig from "@/new_actions/workshop/get_workshop_config";
import type { Employee, Slot, Stage, StageKind, WorkshopConfig, WorkshopLocation } from "@/types/workshop";

/**
 * Workshop configuration for the whole app (branches, stages, slots,
 * employees), loaded once in the root layout. Read it with `useWorkshop()`;
 * call `refresh()` after changing it in Settings.
 */
export type Workshop = WorkshopConfig & {
  /** Active branches in display order. */
  activeLocations: WorkshopLocation[];
  location: (id?: string | null) => WorkshopLocation | undefined;
  stage: (id?: string | null) => Stage | undefined;
  slot: (id?: string | null) => Slot | undefined;
  employee: (id?: string | null) => Employee | undefined;
  /** First stage of a kind (flows like acceptance/release target it). */
  firstStage: (kind: StageKind) => Stage | undefined;
  /** Active slots of one branch, in order. */
  slotsFor: (locationId?: string | null) => Slot[];
  /** Active schedulable employees; with a branch, only those working there. */
  employeesFor: (locationId?: string | null) => Employee[];
  /** The employee linked to a login, if any. */
  employeeForUser: (userId?: string | null) => Employee | undefined;
  refresh: () => Promise<void>;
};

const WorkshopContext = createContext<Workshop | null>(null);

const byOrder = <T extends { order: number }>(a: T, b: T) => a.order - b.order;

export function WorkshopProvider({ initial, children }: { initial: WorkshopConfig; children: React.ReactNode }) {
  const [config, setConfig] = useState(initial);

  const refresh = useCallback(async () => {
    setConfig(await getWorkshopConfig());
  }, []);

  const value = useMemo<Workshop>(() => {
    const index = <T extends { id: string }>(items: T[]) => new Map(items.map((i) => [i.id, i]));
    const locations = [...config.locations].sort(byOrder);
    const stages = [...config.stages].sort(byOrder);
    const slots = [...config.slots].sort(byOrder);
    const employees = [...config.employees].sort(byOrder);
    const locMap = index(locations);
    const stageMap = index(stages);
    const slotMap = index(slots);
    const empMap = index(employees);
    return {
      locations,
      stages,
      slots,
      employees,
      activeLocations: locations.filter((l) => l.active),
      location: (id) => (id ? locMap.get(id) : undefined),
      stage: (id) => (id ? stageMap.get(id) : undefined),
      slot: (id) => (id ? slotMap.get(id) : undefined),
      employee: (id) => (id ? empMap.get(id) : undefined),
      firstStage: (kind) => stages.find((s) => s.kind === kind),
      slotsFor: (locationId) => slots.filter((s) => s.active && (!locationId || s.location_id === locationId)),
      employeesFor: (locationId) =>
        employees.filter(
          (e) => e.active && e.schedulable && (!locationId || e.location_ids.length === 0 || e.location_ids.includes(locationId)),
        ),
      employeeForUser: (userId) => (userId ? employees.find((e) => e.user_id === userId) : undefined),
      refresh,
    };
  }, [config, refresh]);

  return <WorkshopContext.Provider value={value}>{children}</WorkshopContext.Provider>;
}

export function useWorkshop(): Workshop {
  const ctx = useContext(WorkshopContext);
  if (!ctx) throw new Error("useWorkshop() must be used inside <WorkshopProvider>");
  return ctx;
}
